import React from 'react';
import t from 'prop-types';
import { Icon } from './Icon';
import { Tooltip } from './Tooltip';
import { pick } from 'lodash-es';
import classNames from 'classnames';

export const Avatar = ({ tooltip = false, tag = 'span', ...props }) => {
  return tooltip ? (
    <Tooltip
      content={
        typeof tooltip === 'function'
          ? tooltip(pick(props, 'username', 'email', 'displayName')) ||
            props.username
          : props.username
      }
    >
      {({ ref }) => <AvatarComponent {...props} tag={tag} innerRef={ref} />}
    </Tooltip>
  ) : (
    <AvatarComponent {...props} tag={tag} />
  );
};

// Calculate a dynamic font size if an avatar size is provided. Function defined
// from a polynomial regression of the default sizes.
const dynamicFontSize = size =>
  (Math.pow(size, 2) * -0.0025 + size * 0.725 + 0.05).toFixed(1);

const AvatarComponent = ({
  innerRef,
  tag: Tag,
  className,
  displayName,
  email,
  username,
  size,
  sm,
  lg,
}) => {
  const customSize = typeof size === 'number';
  const letter = [displayName, email, username]
    .map(value => (value ? value.charAt(0).toUpperCase() : null))
    .filter(v => v && v.match(/[a-zA-Z]/))[0];

  return (
    <Tag
      ref={innerRef}
      className={classNames(
        'avatar',
        { 'avatar-sm': !customSize && sm, 'avatar-lg': !customSize && lg },
        className,
      )}
      style={
        customSize
          ? {
              width: `${size / 16}rem`,
              height: `${size / 16}rem`,
              fontSize: `${dynamicFontSize(size) / 16}rem`,
            }
          : {}
      }
    >
      {letter || (
        <Icon
          name="user"
          size={customSize ? size / 1.5 : sm ? 16 : lg ? 49 : 25}
        />
      )}
    </Tag>
  );
};

Avatar.propTypes = {
  /** Tag or React component to be used to render the avatar. */
  tag: t.oneOfType([t.string, t.func, t.element]),
  /** Additional classNames to add to the avatar element. */
  className: t.string,
  /**
   * Display name of the user. This is the first property used to determine
   * which letter to show in the avatar.
   */
  displayName: t.string,
  /**
   * Email of the user. This is the second property used to determine which
   * letter to show in the avatar.
   */
  email: t.string,
  /**
   * Username of the user. This is the third property used to determine which
   * letter to show in the avatar.
   */
  username: t.string,
  /**
   * Flag that determines if a tooltip will be visible when hovering over the
   * avatar. If a function is provided, the function will be passed an object
   * with the user properties and the result will be displayed in the tooltip.
   * Otherwise the username will be shown.
   */
  tooltip: t.oneOfType([t.bool, t.func]),
  /**
   * Numeric value for size of the avatar if custom sizes are needed, in pixels.
   * Overrides any of the predefined sizes. Default size is 38px if no sizes are
   * provided.
   */
  size: t.number,
  /** Predefined size for a small avatar (24px). */
  sm: t.bool,
  /** Predefined size for a large icon (74px). */
  lg: t.bool,
  // Numeric value for size of the avatar if custom sizes are needed, in pixels.
};
